// Cattura diretta via getUserMedia: anteprima video live + "Scatta" → JPEG 80.
// Su telefono prova ad accendere la torcia quando browser/WebView
// la espone; la fotocamera di sistema resta disponibile come fallback.
import { useEffect, useRef, useState } from 'react';

type TorchTrack = Pick<MediaStreamTrack, 'applyConstraints'> & {
  getCapabilities?: () => MediaTrackCapabilities & { torch?: boolean };
};

export async function enableCameraTorch(track: TorchTrack | undefined): Promise<boolean> {
  if (!track?.getCapabilities?.().torch) return false;
  try {
    await track.applyConstraints({ advanced: [{ torch: true } as MediaTrackConstraintSet] });
    return true;
  } catch {
    return false;
  }
}

function actionBtn(bg: string, disabled = false): React.CSSProperties {
  return {
    padding: '12px 26px',
    borderRadius: 12,
    border: 'none',
    background: bg,
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  };
}

export function WebcamCaptureModal(props: {
  isOpen: boolean;
  onClose: () => void;
  /** Apre la fotocamera installata quando la cattura diretta o la torcia non sono disponibili. */
  onFallback: () => void;
  /** Restituisce lo scatto come base64 (senza prefisso data:) + mime. */
  onCapture: (b64: string, mime: string) => void;
}): JSX.Element | null {
  const { isOpen, onClose, onFallback, onCapture } = props;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onCloseRef = useRef(onClose);
  const onFallbackRef = useRef(onFallback);
  onCloseRef.current = onClose;
  onFallbackRef.current = onFallback;
  const [shot, setShot] = useState<string | null>(null); // data URL dell'anteprima
  const [ready, setReady] = useState(false);

  function stopStream(): void {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setShot(null);
    setReady(false);
    void (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          onCloseRef.current();
          onFallbackRef.current();
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          // facingMode environment è preferito sui telefoni; su laptop è ignorato
          // (si usa la FaceTime/webcam). Alta risoluzione per leggere meglio il testo.
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        // Richiedi la massima risoluzione esposta dal browser, senza imporre
        // dimensioni che alcune webcam non supportano contemporaneamente.
        const track = stream.getVideoTracks()[0];
        const capabilities = track?.getCapabilities?.();
        if (capabilities?.width?.max && capabilities?.height?.max) {
          await track.applyConstraints({
            width: { ideal: capabilities.width.max },
            height: { ideal: capabilities.height.max },
          }).catch(() => undefined);
        }
        const torchEnabled = await enableCameraTorch(track);
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        if (!torchEnabled) {
          stopStream();
          onCloseRef.current();
          onFallbackRef.current();
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setReady(true);
      } catch {
        onCloseRef.current();
        onFallbackRef.current();
      }
    })();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  function capture(): void {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    setShot(canvas.toDataURL('image/jpeg', 0.8));
  }

  function close(): void {
    stopStream();
    onClose();
  }

  function useShot(): void {
    if (!shot) return;
    const comma = shot.indexOf(',');
    onCapture(comma >= 0 ? shot.slice(comma + 1) : shot, 'image/jpeg');
    close();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(2,6,23,.94)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 16px', color: '#e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>📷 Fotocamera documento</span>
        <button onClick={close} aria-label="Chiudi" style={{ background: 'transparent', border: 'none', color: '#e2e8f0', fontSize: 28, lineHeight: 1, cursor: 'pointer' }}>
          ×
        </button>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: 12 }}>
        {shot ? (
          <img src={shot} alt="Anteprima scatto" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
        ) : (
          <video ref={videoRef} playsInline muted style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
        )}
      </div>
      {!shot && (
        <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, padding: '0 16px 4px' }}>
          {!ready
            ? 'Avvio fotocamera e luce…'
            : '💡 Luce attiva. Evita riflessi e inquadra il foglio a fuoco.'}
        </div>
      )}
      <div style={{ padding: '12px 16px 26px', display: 'flex', gap: 12, justifyContent: 'center' }}>
        {shot ? (
          <>
            <button onClick={() => setShot(null)} style={actionBtn('#334155')}>↻ Rifai</button>
            <button onClick={useShot} style={actionBtn('#16a34a')}>✓ Usa questa</button>
          </>
        ) : (
          <button onClick={capture} disabled={!ready} style={actionBtn('#0066cc', !ready)}>● Scatta</button>
        )}
      </div>
    </div>
  );
}
